import Comment from "./TicketDetails/Comment";
import Contact from "./TicketDetails/Contact";

/**
 * Represents the details of a ticket in a system. This class serves as a model
 * for storing and accessing data related to a ticket, such as author, status,
 * type, description, customer details, comments, associated contacts, and more.
 *
 * Instances of this class are immutable, and data is typically provided
 * upon initialization or through parsing from a JSON source.
 */
class TicketDetails {

    private readonly author: string | null;
    private readonly closingDate: string | null;
    private readonly comments: Comment[] | null;
    private readonly contacts: Contact[] | null;
    private readonly customerId: string | null;
    private readonly customerName: string | null;
    private readonly description: string | null;
    private readonly gtrNok: string | null;
    private readonly link: string | null;
    private readonly linkName: string | null;
    private readonly openingDate: string | null;
    private readonly origin: string | null;
    private readonly siteId: string | null;
    private readonly siteName: string | null;
    private readonly status: string | null;
    private readonly subType: string | null;
    private readonly ticketNumber: string | null;
    private readonly title: string | null;
    private readonly type: string | null;
    private readonly sla: string | null;

    /**
     * Constructor for initializing a ticket object with various details.
     *
     * @param {string|null} author - The author of the ticket.
     * @param {string|null} closingDate - The closing date of the ticket.
     * @param {Comment[]|null} comments - The list of comments associated with the ticket.
     * @param {Contact[]|null} contacts - The list of contacts associated with the ticket.
     * @param {string|null} customerId - The ID of the customer associated with the ticket.
     * @param {string|null} customerName - The name of the customer associated with the ticket.
     * @param {string|null} description - The description of the ticket.
     * @param {string|null} gtrNok - Additional information about the ticket.
     * @param {string|null} link - A link related to the ticket.
     * @param {string|null} linkName - The name or description of the link.
     * @param {string|null} openingDate - The opening date of the ticket.
     * @param {string|null} origin - The origin or source of the ticket.
     * @param {string|null} siteId - The ID of the site associated with the ticket.
     * @param {string|null} siteName - The name of the site associated with the ticket.
     * @param {string|null} status - The current status of the ticket.
     * @param {string|null} subType - The subtype or category of the ticket.
     * @param {string|null} ticketNumber - The unique ticket number.
     * @param {string|null} title - The title of the ticket.
     * @param {string|null} type - The type or classification of the ticket.
     * @param {string|null} sla - The SLA (Service Level Agreement) for the ticket.
     */
    constructor(
        author: string | null = null,
        closingDate: string | null = null,
        comments: Comment[] | null = null,
        contacts: Contact[] | null = null,
        customerId: string | null = null,
        customerName: string | null = null,
        description: string | null = null,
        gtrNok: string | null = null,
        link: string | null = null,
        linkName: string | null = null,
        openingDate: string | null = null,
        origin: string | null = null,
        siteId: string | null = null,
        siteName: string | null = null,
        status: string | null = null,
        subType: string | null = null,
        ticketNumber: string | null = null,
        title: string | null = null,
        type: string | null = null,
        sla: string | null = null,
    ) {
        this.author = author;
        this.closingDate = closingDate;
        this.comments = comments;
        this.contacts = contacts;
        this.customerId = customerId;
        this.customerName = customerName;
        this.description = description;
        this.gtrNok = gtrNok;
        this.link = link;
        this.linkName = linkName;
        this.openingDate = openingDate;
        this.origin = origin;
        this.siteId = siteId;
        this.siteName = siteName;
        this.status = status;
        this.subType = subType;
        this.ticketNumber = ticketNumber;
        this.title = title;
        this.type = type;
        this.sla = sla;
    }

    /**
     * Converts a JSON object into a User instance.
     *
     * @param {any} item - The JSON object containing user data to be converted.
     * @return {User} A new User instance created from the provided JSON data.
     */
    public static fromJson(item: any): TicketDetails {
        return new TicketDetails(
            item.author,
            item.closingDate,
            typeof item.comments !== "undefined" && item.comments !== null
                ? item.comments.map((comment: any) => Comment.fromJson(comment))
                : null,
            typeof item.contacts !== "undefined" && item.contacts !== null
                ? item.contacts.map((contact: any) => Contact.fromJson(contact))
                : null,
            item.customerId,
            item.customerName,
            item.description,
            item.gtrNok,
            item.link,
            item.linkName,
            item.openingDate,
            item.origin,
            item.siteId,
            item.siteName,
            item.status,
            item.subType,
            item.ticketNumber,
            item.title,
            item.type,
            item.sla
        );
    }

    /**
     * Gets the author of the ticket.
     * @returns {string|null} The author's name or null if not set.
     */
    public getAuthor(): string | null {
        return this.author;
    }

    /**
     * Checks whether the author information is present.
     *
     * @return {boolean} Returns true if the author is not null and has a non-empty value; otherwise, returns false.
     */
    public hasAuthor(): boolean {
        return this.author !== null && this.author.length > 0;
    }

    /**
     * Gets the closing date of the ticket.
     * @returns {string|null} The closing date or null if not set.
     */
    public getClosingDate(): string | null {
        return this.closingDate;
    }

    /**
     * Determines whether there are any comments.
     *
     * @return {boolean} True if there are comments; otherwise, false.
     */
    public hasComments(onlyVisible: boolean = true): boolean {
        return this.comments !== null && this.comments.filter((comment: Comment) => onlyVisible ? comment.isVisible() : true).length > 0;
    }

    /**
     * Gets the comments associated with the ticket.
     * @returns {Comment[]} Array of comments or null if not set.
     */
    public getComments(onlyVisible: boolean = true): Comment[] {
        return this.comments === null
            ? []
            : this.comments.filter((comment: Comment) => onlyVisible ? comment.isVisible() : true)
                .filter((comment: Comment) => !comment.getArchived())
            ;
    }

    /**
     * Determines if there are any contacts present.
     *
     * @return {boolean} True if there are contacts, otherwise false.
     */
    public hasContacts(): boolean {
        return this.contacts !== null && this.contacts.length > 0;
    }

    /**
     * Gets the contacts associated with the ticket.
     * @returns {Contact[]} Array of contacts or null if not set.
     */
    public getContacts(): Contact[] {
        return this.contacts === null ? [] : this.contacts;
    }

    /**
     * Gets the customer ID associated with the ticket.
     * @returns {string|null} The customer ID or null if not set.
     */
    public getCustomerId(): string | null {
        return this.customerId;
    }

    /**
     * Gets the customer name associated with the ticket.
     * @returns {string|null} The customer name or null if not set.
     */
    public getCustomerName(): string | null {
        return this.customerName;
    }

    /**
     * Determines whether the customer name is defined and non-empty.
     *
     * @return {boolean} True if the customer name exists and is not empty, otherwise false.
     */
    public hasCustomerName(): boolean {
        return this.customerName !== null && this.customerName.length > 0;
    }

    /**
     * Gets the description of the ticket.
     * @returns {string|null} The description or null if not set.
     */
    public getDescription(): string | null {
        return this.description;
    }

    /**
     * Determines if the current instance has a non-empty description.
     *
     * @return {boolean} True if a description exists and is not empty, otherwise false.
     */
    public hasDescription(): boolean {
        return this.description !== null && this.description.length > 0;
    }

    /**
     * Gets the GTR NOK value of the ticket.
     * @returns {string|null} The GTR NOK value or null if not set.
     */
    public getGtrNok(): string | null {
        return this.gtrNok;
    }

    /**
     * Checks whether the `gtrNok` property is not null and has a length greater than 0.
     *
     * @return {boolean} Returns true if `gtrNok` is not null and contains one or more elements; otherwise, returns false.
     */
    public hasGtrNok(): boolean {
        return this.gtrNok !== null && this.gtrNok.length > 0;
    }

    /**
     * Gets the link associated with the ticket.
     * @returns {string|null} The link or null if not set.
     */
    public getLink(): string | null {
        return this.link;
    }

    /**
     * Checks if there is a valid link available.
     *
     * @return {boolean} True if a non-null, non-empty link exists; otherwise, false.
     */
    public hasLink(): boolean {
        return this.link !== null && this.link.length > 0;
    }

    /**
     * Gets the link name associated with the ticket.
     * @returns {string|null} The link name or null if not set.
     */
    public getLinkName(): string | null {
        return this.linkName;
    }

    /**
     * Gets the opening date of the ticket.
     * @returns {string|null} The opening date or null if not set.
     */
    public getOpeningDate(): string | null {
        return this.openingDate;
    }

    /**
     * Gets the origin of the ticket.
     * @returns {string|null} The origin or null if not set.
     */
    public getOrigin(): string | null {
        return this.origin;
    }

    /**
     * Gets the site ID associated with the ticket.
     * @returns {string|null} The site ID or null if not set.
     */
    public getSiteId(): string | null {
        return this.siteId;
    }

    /**
     * Gets the site name associated with the ticket.
     * @returns {string|null} The site name or null if not set.
     */
    public getSiteName(): string | null {
        return this.siteName;
    }

    /**
     * Gets the status of the ticket.
     * @returns {string|null} The status or null if not set.
     */
    public getStatus(): string | null {
        return this.status;
    }

    /**
     * Gets the subtype of the ticket.
     * @returns {string|null} The subtype or null if not set.
     */
    public getSubType(): string | null {
        return this.subType;
    }

    /**
     * Gets the ticket number.
     * @returns {string|null} The ticket number or null if not set.
     */
    public getTicketNumber(): string | null {
        return this.ticketNumber;
    }

    /**
     * Gets the title of the ticket.
     * @returns {string|null} The title or null if not set.
     */
    public getTitle(): string | null {
        return this.title;
    }

    /**
     * Gets the type of the ticket.
     * @returns {string|null} The type or null if not set.
     */
    public getType(): string | null {
        return this.type;
    }

    public getSla(): string | null {
        return this.sla;
    }

    public hasSla(): boolean {
        return this.sla !== null && this.sla.length > 0;
    }

}

export default TicketDetails;